// =============================================================================
// lib/watermark.ts — shared TimestampCamera-style canvas watermark.
//
// White Lato Regular text bottom-left with soft Gaussian-blurred shadow.
// Format per line:
//   1. 21/5/2026, 3:25 pm           (12h, lowercase am/pm, no zero-pad d/m)
//   2. -31.563255,+147.192571       (signed decimals, NO space after comma)
//   3..N. address lines             (filled in Commit 2 via /geocode/reverse)
//
// Burns into JPEG bytes (reverses T-217 clean-bytes regime). Re-injects EXIF
// DateTimeOriginal + GPS via piexifjs so Ventia Golden PP "metadata
// available in the file" requirement is met.
//
// T-217 strict data rule preserved: caller decides whether takenAt+gps come
// from EXIF (library upload) or live capture (precomputedGps + new Date()).
// If a value is null we omit that watermark line — never burn upload-time
// or device-location fallbacks. Missing data → blank, never wrong.
// =============================================================================
import piexif from "piexifjs";

export interface WatermarkOpts {
  takenAt: Date | null;
  gps: { lat: number; lng: number } | null;
  /** Reverse-geocoded address lines — added in Commit 2; empty/undefined in Commit 1 */
  addressLines?: string[];
  /** 10 Oct 2026 (mig 441) — shrink the picture so it has no more than this
   *  many pixels (aspect kept). The stamp is laid out on the SHRUNK picture,
   *  so it reads the same as on a full-size one. Unset = full size. */
  maxPixels?: number;
  /** JPEG quality, default 0.92. */
  quality?: number;
}

export interface WatermarkResult {
  blob: Blob;
  width: number;
  height: number;
  mimeType: "image/jpeg";
  watermarked: boolean;
  /** mig 441 — the picture was shrunk to fit maxPixels (a full-size copy is worth sending too). */
  shrunk?: boolean;
}

const FONT_URL = "/fonts/Lato-Regular.ttf";
const FONT_FAMILY = "LatoWatermark";

// Idempotent font load via FontFace API. First call kicks off the fetch; later
// calls return the same promise. Browsers without FontFace (very old) silently
// fall back to the canvas font stack (Arial / sans-serif).
//
// 10 Oct 2026 (Nullarbor): this sat in front of EVERY photo with no timeout
// and no catch. The font is a 660 KB download that was not in the offline
// cache, so on one bar the first photo of a session waited on it forever, and
// with no signal the failed load was remembered — every later photo in that
// session then failed its watermark until the app was reloaded. Now: 4 seconds
// at most, a failure is never remembered (the next photo tries again), and
// the photo is stamped in the fallback font rather than not at all. The font
// is also in the offline cache from this release (vite.config.ts).
let fontLoadPromise: Promise<void> | null = null;
function ensureFont(): Promise<void> {
  if (fontLoadPromise) return fontLoadPromise;
  const attempt = (async () => {
    if (typeof (globalThis as any).FontFace === "undefined") return;
    const face = new FontFace(FONT_FAMILY, `url(${FONT_URL})`);
    await Promise.race([
      face.load(),
      new Promise((_, rej) => setTimeout(() => rej(new Error("font_timeout")), 4_000)),
    ]);
    (document as any).fonts.add(face);
  })();
  fontLoadPromise = attempt.catch((e) => {
    console.warn("[watermark] font not loaded — using the fallback font for this photo", e);
    fontLoadPromise = null;          // try again on the next photo
  });
  return fontLoadPromise;
}

function loadImage(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function fmtDateTime(d: Date): string {
  // TimestampCamera format: '21/5/2026, 3:25 pm'
  //   - no zero-pad on day or month
  //   - 12-hour clock, lowercase am/pm
  //   - zero-padded minutes
  let h = d.getHours();
  const ampm = h < 12 ? "am" : "pm";
  h = h % 12; if (h === 0) h = 12;
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}, ${h}:${m} ${ampm}`;
}

function fmtCoord(v: number): string {
  // Signed, 6 decimals, no degree symbol. Matches TimestampCamera reference.
  const sign = v >= 0 ? "+" : "-";
  return `${sign}${Math.abs(v).toFixed(6)}`;
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas_toBlob_failed"))),
      "image/jpeg",
      quality,
    );
  });
}

// -----------------------------------------------------------------------------
// EXIF re-injection via piexifjs.
//
// piexifjs operates on base64-encoded JPEG data URIs (legacy API). We:
//   1. Read the canvas-exported blob into a Uint8Array
//   2. Build a data: URI with btoa() over the binary string (chunked to avoid
//      stack overflow on big blobs)
//   3. Dump opts → EXIF dict → bytes
//   4. Call piexif.insert(exifBytes, dataUri) → new dataUri
//   5. Convert back to Blob
// Round-trip is ~20-50ms for a 4MB JPEG on a modern phone. Acceptable.
// -----------------------------------------------------------------------------
async function reinjectExif(blob: Blob, opts: WatermarkOpts): Promise<Blob> {
  const arrBuf = await blob.arrayBuffer();
  const u8 = new Uint8Array(arrBuf);
  // Build binary string in 32KB chunks to avoid Array.from blowing call stack
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < u8.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, u8.subarray(i, i + CHUNK) as any);
  }
  const dataUri = "data:image/jpeg;base64," + btoa(bin);

  const exif: any = { "0th": {}, "Exif": {}, "GPS": {}, "1st": {}, "thumbnail": null };

  if (opts.takenAt) {
    const d = opts.takenAt;
    const p2 = (n: number) => String(n).padStart(2, "0");
    const dStr = `${d.getFullYear()}:${p2(d.getMonth() + 1)}:${p2(d.getDate())} `
               + `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
    exif["Exif"][piexif.ExifIFD.DateTimeOriginal] = dStr;
    exif["Exif"][piexif.ExifIFD.DateTimeDigitized] = dStr;
    exif["0th"][piexif.ImageIFD.DateTime] = dStr;
  }

  if (opts.gps) {
    // DMS rationals: degrees int/1, minutes int/1, seconds hundredths/100
    const toDms = (n: number) => {
      const abs = Math.abs(n);
      const dg = Math.floor(abs);
      const mFloat = (abs - dg) * 60;
      const mi = Math.floor(mFloat);
      const sec = Math.round((mFloat - mi) * 60 * 100);
      return [[dg, 1], [mi, 1], [sec, 100]];
    };
    exif["GPS"][piexif.GPSIFD.GPSLatitudeRef]  = opts.gps.lat >= 0 ? "N" : "S";
    exif["GPS"][piexif.GPSIFD.GPSLatitude]     = toDms(opts.gps.lat);
    exif["GPS"][piexif.GPSIFD.GPSLongitudeRef] = opts.gps.lng >= 0 ? "E" : "W";
    exif["GPS"][piexif.GPSIFD.GPSLongitude]    = toDms(opts.gps.lng);
  }

  const exifBytes = piexif.dump(exif);
  const newDataUri = piexif.insert(exifBytes, dataUri);

  // dataUri → Blob
  const b64 = newDataUri.split(",")[1];
  const newBin = atob(b64);
  const newU8 = new Uint8Array(newBin.length);
  for (let i = 0; i < newBin.length; i++) newU8[i] = newBin.charCodeAt(i);
  return new Blob([newU8], { type: "image/jpeg" });
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------
export async function watermarkPhoto(file: File, opts: WatermarkOpts): Promise<WatermarkResult> {
  await ensureFont();

  // Build lines — T-217 strict rule: missing data omits the line entirely
  const lines: string[] = [];
  if (opts.takenAt) lines.push(fmtDateTime(opts.takenAt));
  if (opts.gps)     lines.push(`${fmtCoord(opts.gps.lat)},${fmtCoord(opts.gps.lng)}`);
  if (opts.addressLines && opts.addressLines.length) lines.push(...opts.addressLines);

  // Decode + draw
  const img = await loadImage(file);
  const srcW = img.naturalWidth  || img.width;
  const srcH = img.naturalHeight || img.height;
  // mig 441 — the quick copy: scale down to fit the pixel budget, never up.
  const fit = opts.maxPixels && srcW * srcH > opts.maxPixels ? Math.sqrt(opts.maxPixels / (srcW * srcH)) : 1;
  const w = Math.max(1, Math.floor(srcW * fit));
  const h = Math.max(1, Math.floor(srcH * fit));
  const quality = opts.quality ?? 0.92;
  const canvas = document.createElement("canvas");
  canvas.width  = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_2d_unavailable");
  ctx.drawImage(img, 0, 0, w, h);

  // Empty lines → JPEG passthrough (still normalises HEIC/PNG → JPEG)
  if (lines.length === 0) {
    const blob = await canvasToJpeg(canvas, quality);
    return { blob, width: w, height: h, mimeType: "image/jpeg", watermarked: false, shrunk: fit < 1 };
  }

  // Layout — matches the Pillow render James signed off on (Lato Regular,
  // ~2.0% of H font size, soft blurred shadow, bottom-left).
  const fsize   = Math.max(24, Math.round(h * 0.020));
  const marginX = Math.round(w * 0.022);
  const marginY = Math.round(w * 0.022);  // intentionally w, matches Pillow margins
  const lineGap = Math.round(fsize * 0.20);
  const totalH  = lines.length * fsize + (lines.length - 1) * lineGap;
  const y0      = h - marginY - totalH;
  const fontSpec = `${fsize}px "${FONT_FAMILY}", Arial, sans-serif`;

  ctx.textBaseline = "top";

  // Shadow layer — draw text to offscreen canvas, blur via filter when composited
  const shadowCanvas = document.createElement("canvas");
  shadowCanvas.width  = w;
  shadowCanvas.height = h;
  const sctx = shadowCanvas.getContext("2d");
  if (!sctx) throw new Error("canvas_2d_unavailable");
  sctx.font = fontSpec;
  sctx.textBaseline = "top";
  sctx.fillStyle = "rgba(0,0,0,0.80)";
  let yy = y0;
  for (const line of lines) {
    sctx.fillText(line, marginX, yy);
    yy += fsize + lineGap;
  }
  // Composite blurred shadow onto main canvas
  ctx.save();
  (ctx as any).filter = `blur(${Math.max(2, Math.round(fsize * 0.10))}px)`;
  ctx.drawImage(shadowCanvas, 0, 0);
  ctx.restore();

  // Crisp white text on top
  ctx.font = fontSpec;
  ctx.fillStyle = "rgba(255,255,255,1)";
  yy = y0;
  for (const line of lines) {
    ctx.fillText(line, marginX, yy);
    yy += fsize + lineGap;
  }

  // Export + EXIF re-inject (Ventia Golden PP requirement)
  let blob = await canvasToJpeg(canvas, quality);
  if (opts.takenAt || opts.gps) {
    blob = await reinjectExif(blob, opts);
  }

  return { blob, width: w, height: h, mimeType: "image/jpeg", watermarked: true, shrunk: fit < 1 };
}
